import type { DOMOutputSpec, Node as ProsemirrorNode, Node, NodeSpec } from 'prosemirror-model';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { MentionElement, MentionElementGroup } from './types';
import { MentionType } from './types';
import { NodeName } from '../../schema/nodes';

type MentionTypeElementMap = Record<MentionType, Record<number, MentionElement>>;

export class MentionNode implements NodeSpec {
  public static readonly nodeName = NodeName.Mention;
  public static is(node: ProsemirrorNode) {
    return node.type.name === MentionNode.nodeName;
  }
  public attrs = {
    'data-id': {},
    'data-type': {},
  };
  public inline = true;
  public atom = true;
  public content = 'inline+';
  public group = 'inline';
  public selectable = false;
  public draggable = false;
  public parseDOM = [
    {
      tag: 'span[data-component-type="mention"]',
      getAttrs: (node: HTMLElement) => ({
        'data-id': parseInt(node.dataset['id']!, 10),
        'data-type': node.dataset['type'],
      }),
    },
  ];

  private destroy$ = new Subject<void>();
  private idDataMap: MentionTypeElementMap = {
    [MentionType.User]: {},
    [MentionType.Role]: {},
  };

  constructor(
    mentionViewOnly: boolean,
    private dataSource?: Observable<MentionElement[] | MentionElementGroup[]>,
  ) {
    if (mentionViewOnly) {
      this.parseDOM = [];
    }
    if (this.dataSource) {
      this.dataSource.pipe(takeUntil(this.destroy$)).subscribe(data => {
        if (data.some(item => 'data' in item)) {
          this.idDataMap = data
            .flatMap(group => (<MentionElementGroup>group).data)
            .reduce(
              (acc, item) => {
                if (!acc[item.type]) acc[item.type] = {};
                acc[item.type][item.id] = item;
                return acc;
              },
              <MentionTypeElementMap>{},
            );
        } else {
          this.idDataMap = (<MentionElement[]>data).reduce(
            (acc, item) => {
              if (!acc[item.type]) acc[item.type] = {};
              acc[item.type][item.id] = item;
              return acc;
            },
            <MentionTypeElementMap>{},
          );
        }
      });
    }
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toDOM = (node: Node): DOMOutputSpec => {
    const id = parseInt(node.attrs['data-id'], 10);
    const type: MentionType = node.attrs['data-type'];
    const data = this.getNodeData(id, type);
    const text = data ? `@${data.name}` : `@${node.textContent.replace(/^@/, '')}`;

    return [
      'span',
      {
        'data-id': `${id}`,
        'data-type': type,
        'data-component-type': 'mention',
        style: `color: --color-text-primary`,
      },
      text,
    ];
  };

  private getNodeData(id: number, type: MentionType): MentionElement | null {
    return this.idDataMap?.[type]?.[id] || null;
  }
}

export class LegacyMentionNode implements NodeSpec {
  public static readonly nodeName = NodeName.LegacyMention;
  public inline = true;
  public atom = true;
  public content = 'inline+';
  public group = 'inline';
  public selectable = true;
  public draggable = false;
  public parseDOM = [
    {
      tag: 'span[class="mention"]',
    },
  ];

  constructor(mentionViewOnly: boolean) {
    if (mentionViewOnly) {
      this.parseDOM = [];
    }
  }

  public toDOM(node: Node): DOMOutputSpec {
    return ['span', { class: 'mention' }, node.textContent];
  }
}
